import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export interface RequestFileItem {
  id: number;
  url: string;
  fileName?: string | null;
}

type PreviewKind = 'image' | 'pdf' | 'text' | 'word' | 'unknown';

@Component({
  selector: 'app-request-file-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './request-file-preview.component.html',
  styleUrls: ['./request-file-preview.component.scss'],
})
export class RequestFilePreviewComponent {
  @Input() files: RequestFileItem[] = [];

  selectedFile: RequestFileItem | null = null;

  constructor(private readonly sanitizer: DomSanitizer) {}

  openPreview(file: RequestFileItem): void {
    this.selectedFile = file;
  }

  closePreview(): void {
    this.selectedFile = null;
  }

  getFileName(file: RequestFileItem): string {
    if (file.fileName?.trim()) {
      return file.fileName.trim();
    }

    try {
      const path = new URL(file.url).pathname;
      const last = path.split('/').pop();
      return decodeURIComponent(last || 'File');
    } catch {
      return 'File';
    }
  }

  getPreviewKind(file: RequestFileItem): PreviewKind {
    const lower = this.getFileName(file).toLowerCase();

    if (/\.(png|jpg|jpeg|gif|bmp|webp|svg)$/.test(lower)) return 'image';
    if (/\.pdf$/.test(lower)) return 'pdf';
    if (/\.(txt|md|log|rtf)$/.test(lower)) return 'text';
    if (/\.(doc|docx)$/.test(lower)) return 'word';

    return 'unknown';
  }

  getIframeSrc(file: RequestFileItem): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(file.url);
  }

  getWordIframeSrc(file: RequestFileItem): SafeResourceUrl {
    const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(officeUrl);
  }
}
